import { useEffect } from "react";
import { optimizedImage } from "@/lib/cloudinaryImage";

/**
 * Full-screen image lightbox.
 * Tap anywhere outside the image / press ESC / tap × to close.
 * Pulls a high-res Cloudinary variant (w_1200) when available.
 */
export const ImageLightbox = ({ src, alt, onClose }) => {
    // ESC to close + lock body scroll while open
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    if (!src) return null;
    const hiRes = optimizedImage(src, { w: 1200, crop: false });

    return (
        <div
            className="fixed inset-0 z-[60] bg-black/95 grid place-items-center p-4 animate-fade-in"
            onClick={onClose}
            data-testid="image-lightbox"
            role="dialog"
            aria-modal="true"
        >
            <button
                onClick={onClose}
                className="absolute top-4 right-5 w-10 h-10 grid place-items-center rounded-full bg-white/10 text-white font-mono text-lg hover:bg-white/20"
                data-testid="image-lightbox-close"
                aria-label="Close"
            >
                ✕
            </button>
            <img
                src={hiRes}
                alt={alt || ""}
                onClick={(e) => e.stopPropagation()}
                className="max-w-full max-h-[88vh] object-contain shadow-2xl"
                data-testid="image-lightbox-img"
            />
            {alt && (
                <p
                    className="absolute bottom-4 left-0 right-0 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-white/70 px-6"
                    onClick={(e) => e.stopPropagation()}
                >
                    {alt}
                </p>
            )}
        </div>
    );
};

export default ImageLightbox;
